import React, { useState, useEffect, useCallback } from 'react'
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Search, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useMessage } from '../../contexts/MessageContext'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { useApi } from '../../utils/api'
import { PageTitleBar } from '../../components/PageTitleBar'
import { ListTable } from '../../components/ListTable'

interface AdminUser {
  id: number;
  name: string;
  email: string;
  admin_level: string;
  is_active: number;
}

interface ApiResponse {
  data: {
    users: AdminUser[];
  };
  success: boolean;
}

export default function AdminUsersList() {
  const { message, setMessage } = useMessage()
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { fetchWithAuth } = useApi()

  const handleDeleteUser = (id: number) => {
    setUserToDelete(id)
    setDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setDeleteModalOpen(false)
    setUserToDelete(null)
  }

  const confirmDelete = async () => {
    if (!userToDelete) return

    setIsDeleting(true)
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/admin-users/${userToDelete}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Failed to delete admin user')
      }

      setAdminUsers(adminUsers.filter(user => user.id !== userToDelete))
      setMessage('Admin user deleted successfully')
      closeDeleteModal()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsDeleting(false)
    }
  }

  const fetchAdminUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth('/admin/admin-users');
      if (!response.ok) {
        throw new Error('Failed to fetch admin users');
      }
      const data = await response.json();
      // The API returns an array directly, not wrapped in a data object
      if (Array.isArray(data)) {
        setAdminUsers(data);
      } else {
        console.error('Unexpected data format:', data);
        setAdminUsers([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchAdminUsers()
  }, [fetchAdminUsers])

  const filteredUsers = adminUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleEditUser = (id: number) => {
    navigate(`/edit-admin-user/${id}`)
  }

  const formatAdminLevel = (level: string) => {
    return level.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const columns = [
    { header: 'Name', accessor: 'name' },
    { header: 'Email', accessor: 'email' },
    { header: 'Type', accessor: 'admin_level' },
  ]

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>
  }

  return (
    <div className="min-h-screen bg-gray-100 text-[#3e3d3d]">
      
      <main className="container mx-auto px-4 py-8">
        {message && (
          <div className="mb-4 p-4 bg-[#8bcfc2] border border-[#3eaf99] text-[#0b677c] rounded">
            {message}
          </div>
        )}

        <PageTitleBar
          title="Admin Users"
          breadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Admin Users' },
          ]}
          actionLabel="New User"
          actionHref="/new-admin-user"
        />

        <div className="bg-white rounded-sm shadow-md">
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search admin users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border-gray-300 focus:border-[#3eaf99] focus:ring focus:ring-[#3eaf99] focus:ring-opacity-50"
              />
            </div>
          </div>

          <ListTable
            columns={columns}
            data={filteredUsers}
            editPathPrefix="/edit-admin-user"
            rowActions={(user) => (
              <div className="flex space-x-4">
                <button
                  onClick={() => handleEditUser(user.id)}
                  className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id)}
                  className="text-xs font-semibold uppercase text-gray-600 hover:text-gray-900"
                >
                  Delete
                </button>
              </div>
            )}
          />
        </div>

        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="bg-white rounded-md shadow-lg p-6 max-w-md mx-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">Delete admin user?</DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-sm text-gray-500">This action cannot be undone.</p>
            <DialogFooter className="mt-6 flex justify-end space-x-2">
              <Button
                variant="ghost"
                onClick={closeDeleteModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}
